import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CollectiveProductContext } from "@toolkit/Framework/Components/ProductContext/CollectiveProductContext";
import { GetProductContextProps } from "@toolkit/Framework/Components/ProductContext/GetProductContext";
import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    products?: Array<ProductModel | null | undefined>;
}

type Props = OwnProps & GetProductContextProps & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    loadRealTimePricing,
    loadRealTimeInventory,
};

const GetCollectiveProductContext: FC<PropsWithChildren<Props>> = ({
    products,
    loadPrice = true,
    loadInventory = true,
    children,
    loadRealTimePricing,
    loadRealTimeInventory,
}) => {
    const initialContext: Array<ProductContextModel> = [];
    let mounted = true;

    products?.forEach(product => {
        if (product) {
            initialContext.push({
                product,
                productInfo: createFromProduct(product),
            });
        }
    });

    const [collectiveContext, setCollectiveContext] = useState<Array<ProductContextModel>>(initialContext);
    const _updateContext = (collectiveContext: Array<ProductContextModel>) => {
        if (mounted) {
            setCollectiveContext(collectiveContext);
        }
    };

    useEffect(() => {
        if (collectiveContext.length) {
            if (loadPrice) {
                loadRealTimePricing({
                    productPriceParameters: collectiveContext.map(x => x.productInfo),
                    onSuccess: result => {
                        _updateContext(
                            collectiveContext.map(context => {
                                const pricing = result.realTimePricingResults?.find(
                                    x => x.productId === context.product.id,
                                );

                                if (pricing) {
                                    return {
                                        ...context,
                                        productInfo: {
                                            ...context.productInfo,
                                            pricing,
                                        },
                                    };
                                }

                                return context;
                            }),
                        );
                    },
                    onError: () => {
                        _updateContext(
                            collectiveContext.map(context => ({
                                ...context,
                                productInfo: {
                                    ...context.productInfo,
                                    failedToLoadPricing: true,
                                },
                            })),
                        );
                    },
                });
            }

            if (loadInventory) {
                loadRealTimeInventory({
                    productIds: collectiveContext.map(x => x.product.id),
                    onSuccess: result => {
                        _updateContext(
                            collectiveContext.map(context => {
                                const inventory = result.realTimeInventoryResults?.find(
                                    x => x.productId === context.product.id,
                                );

                                if (inventory) {
                                    return {
                                        ...context,
                                        productInfo: {
                                            ...context.productInfo,
                                            inventory,
                                        },
                                    };
                                }

                                return context;
                            }),
                        );
                    },
                });
            }
        }

        return () => {
            mounted = false;
        };
    }, []);

    return <CollectiveProductContext.Provider value={collectiveContext}>{children}</CollectiveProductContext.Provider>;
};

export default connect(null, mapDispatchToProps)(GetCollectiveProductContext);
